import { useText } from "@/lib/content";
import { AlertCircle } from "lucide-react";
import { PasswordStrength } from "@/components/form/PasswordStrength";
import { PasswordInput } from "@/components/form/PasswordInput";
import { PrivacyLink } from "@/lib/site";
import { SpinSelect } from "@/components/form/SpinSelect";

const describedBy = (key, f, error) =>
    [f.help_text ? `${key}-help` : null, f.validator === "password" ? `${key}-strength` : null, error ? `${key}-error` : null].filter(Boolean).join(" ") || undefined;

const INPUT_TYPES = { text: "text", email: "email", tel: "tel", url: "text", password: "password", number: "text" };

export const FieldError = ({ id, error }) =>
    error ? (
        <p id={id} className="edi-error" data-testid={`${id}`}>
            <AlertCircle aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
        </p>
    ) : null;

export const ConfigField = ({ field, value, error, onChange, provinces, spins, readOnly, spinHint, email }) => {
    const t = useText();
    const key = field.key;
    const id = `field-${key}`;
    const common = {
        id,
        name: key,
        "aria-invalid": error ? "true" : "false",
        "aria-describedby": describedBy(id, field, error),
        "aria-required": field.required ? "true" : undefined,
        "data-testid": `${id}-input`,
    };

    if (field.type === "checkbox") {
        return (
            <div>
                <div className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1 h-5 w-5 accent-[var(--edi-primary)]" checked={value === true} onChange={(e) => onChange(key, e.target.checked)} {...common} />
                    <label htmlFor={id} className="font-medium">
                        {field.label} {field.required && <span aria-hidden="true">*</span>}{" "}
                        <PrivacyLink testId="register-privacy-info-link">{t("auth.privacy_link")}</PrivacyLink>
                    </label>
                </div>
                <FieldError id={`${id}-error`} error={error} />
            </div>
        );
    }

    const label = (
        <label htmlFor={id} className="edi-label">
            {field.label} {field.required ? <span aria-hidden="true" className="text-edi-error">*</span> : <span className="text-edi-muted font-normal">(facoltativo)</span>}
        </label>
    );
    let control;
    if (field.type === "textarea") {
        control = <textarea className="edi-input min-h-[100px]" value={value || ""} onChange={(e) => onChange(key, e.target.value)} readOnly={readOnly} {...common} />;
    } else if (["select", "province", "boolean"].includes(field.type)) {
        const options = field.type === "boolean" ? [{ value: "Sì", label: "Sì" }, { value: "No", label: "No" }] : field.type === "province"
            ? provinces.map((p) => ({ value: p.sigla, label: `${p.name} (${p.sigla})` }))
            : field.options;
        control = (
            <select className="edi-input" value={value || ""} onChange={(e) => onChange(key, e.target.value)} disabled={readOnly} {...common}>
                <option value="">Seleziona…</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {value && !options.some((o) => o.value === value) && <option value={value}>{value}</option>}
            </select>
        );
    } else if (field.type === "spin") {
        control = <SpinSelect value={value} onChange={(v) => onChange(key, v)} spins={spins} matches={spinHint?.matches || []} common={{ ...common, disabled: readOnly }} />;
    } else {
        const Tag = field.type === "password" ? PasswordInput : "input";
        control = (
            <Tag
                type={INPUT_TYPES[field.type] || "text"}
                className="edi-input"
                value={value || ""}
                onChange={(e) => onChange(key, e.target.value)}
                autoComplete={field.autocomplete || undefined}
                inputMode={field.inputmode || (field.type === "url" ? "url" : field.type === "number" ? "decimal" : undefined)}
                readOnly={readOnly}
                aria-readonly={readOnly ? "true" : undefined}
                {...common}
            />
        );
    }

    return (
        <div>
            {label}
            {control}
            {field.validator === "password" && <PasswordStrength id={`${id}-strength`} password={value || ""} email={email} />}
            {field.type === "spin" && spinHint?.none && (
                <p className="edi-help font-medium text-edi-warning-text bg-edi-warning-bg rounded px-2 py-1" data-testid="spin-no-coverage-message" id={`${id}-nocoverage`}>
                    Nella tua provincia non è presente uno sportello SPIN: scegli quello più vicino
                </p>
            )}
            {readOnly && <p className="edi-help">Non modificabile</p>}
            {field.help_text && <p id={`${id}-help`} className="edi-help">{field.help_text}</p>}
            <FieldError id={`${id}-error`} error={error} />
        </div>
    );
};
